import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from "react";
import { useThemeColors, useThemeFonts } from "@brandclub/bc-widgets";
import { useVoiceSession } from "./hooks/useVoiceSession.js";
import { useVoiceSoundEffects } from "./hooks/useVoiceSoundEffects.js";
import { useIsCompactVoiceLayout } from "./hooks/useIsCompactVoiceLayout.js";
import { VoiceBlob } from "./components/Blob/index.js";
import { ControlBar } from "./components/ControlBar.js";
import { VoiceBottomDock } from "./components/VoiceBottomDock.js";
import { VoiceScrollList } from "./components/VoiceScrollList.js";
import { VoiceThemeProvider } from "./theme/VoiceThemeProvider.js";
function derivePhaseBucket(phase) {
  if (phase === "live") return "live";
  return "connecting";
}
function deriveBlobState(args) {
  if (args.phase === "error") return "error";
  if (args.phase === "ended") return "ended";
  if (args.phase === "requesting-permission" || args.phase === "connecting" || args.phase === "ending") {
    return "connecting";
  }
  if (args.phase !== "live") return "connecting";
  if (args.muted) return "muted";
  if (args.hasStatus) return "tool";
  if (args.isUserSpeaking) return "listening";
  return "idle";
}
function deriveStatusLabel(args) {
  switch (args.phase) {
    case "idle":
      return { label: "Starting…", tone: "idle" };
    case "requesting-permission":
      return { label: "Microphone permission…", tone: "connecting" };
    case "connecting":
      return { label: "Connecting…", tone: "connecting" };
    case "ending":
      return { label: "Ending…", tone: "idle" };
    case "ended":
      return { label: "", tone: "idle" };
    case "error":
      return { label: args.errorMsg || "Error", tone: "error" };
    case "live":
      if (args.muted) return { label: "Muted", tone: "muted" };
      if (args.toolStatus) return { label: args.toolStatus, tone: "live" };
      return { label: "", tone: "live" };
    default:
      return { label: "", tone: "idle" };
  }
}
const VoiceCallOverlayInner = ({ conversationId, pdpStacklineSku, containerScoped, onEnded, onUserEndClick }) => {
  var _a, _b;
  const errorHeadline = "Voice mode unavailable";
  const isCompact = useIsCompactVoiceLayout();
  const sfx = useVoiceSoundEffects();
  const handleEnded = useCallback(
    (info) => {
      sfx.playEndCall();
      onEnded(info);
    },
    [onEnded, sfx]
  );
  const session = useVoiceSession({ conversationId, pdpStacklineSku, onEnded: handleEnded });
  const toolStatus = (_b = (_a = session.lastStatusEvent) == null ? void 0 : _a.data) == null ? void 0 : _b.status;
  const shouldPlayToolLoop = session.phase === "live" && session.toolCallsInFlight > 0 && !session.isAssistantAudible;
  useEffect(() => {
    if (shouldPlayToolLoop) {
      sfx.startToolLoop();
    } else {
      sfx.stopToolLoop();
    }
  }, [shouldPlayToolLoop, sfx]);
  const phaseBucket = derivePhaseBucket(session.phase);
  const blobState = useMemo(
    () => deriveBlobState({
      phase: session.phase,
      muted: session.muted,
      isUserSpeaking: session.isUserSpeaking,
      hasStatus: !!toolStatus
    }),
    [session.phase, session.muted, session.isUserSpeaking, toolStatus]
  );
  const { label: statusLabel, tone: statusTone } = useMemo(
    () => deriveStatusLabel({
      phase: session.phase,
      muted: session.muted,
      toolStatus,
      errorMsg: session.error
    }),
    [session.phase, session.muted, toolStatus, session.error]
  );
  const controlsDisabled = session.phase !== "live";
  const footerStatusLabel = phaseBucket === "live" ? statusLabel : "";
  const handleEnd = useCallback(async () => {
    await session.end("user_ended");
  }, [session]);
  return /* @__PURE__ */ jsxs("div", { style: pageStyle, children: [
    phaseBucket === "connecting" ? /* @__PURE__ */ jsx(
      ConnectingView,
      {
        blobState,
        label: statusLabel,
        tone: statusTone,
        isCompact
      }
    ) : /* @__PURE__ */ jsxs("div", { style: liveScrollWrapStyle, children: [
      /* @__PURE__ */ jsx(
        VoiceScrollList,
        {
          lines: session.lines,
          widgetEvents: session.widgetEvents,
          onLoginCompleted: session.notifyLoginCompleted,
          onLoginFailed: session.notifyLoginFailed
        }
      ),
      /* @__PURE__ */ jsx(VoiceBottomDock, { blobState })
    ] }),
    /* @__PURE__ */ jsx(
      ControlBar,
      {
        muted: session.muted,
        onToggleMute: () => session.setMuted(!session.muted),
        onEnd: handleEnd,
        seconds: session.seconds,
        active: session.isAssistantSpeaking || session.isUserSpeaking,
        disabled: controlsDisabled,
        statusLabel: footerStatusLabel,
        statusTone
      }
    ),
    session.phase === "error" && /* @__PURE__ */ jsx(
      Overlay,
      {
        title: errorHeadline,
        body: session.error ?? "Could not start a call.",
        primary: { label: "Back to chat", onClick: onUserEndClick },
        containerScoped
      }
    )
  ] });
};
const BLOB_SIZE = 100;
const ConnectingView = ({ blobState, label, tone, isCompact }) => {
  const { themeColors } = useThemeColors();
  const { themeFonts } = useThemeFonts();
  const textColor = themeColors.textPrimary || "#374151";
  const bodyFont = themeFonts == null ? void 0 : themeFonts.body;
  const fontFamily = bodyFont ? bodyFont.format === "system" ? bodyFont.url : `"${bodyFont.name}", sans-serif` : "inherit";
  const dotColor = tone === "connecting" ? "var(--voice-status-connecting-dot, #A7C7E7)" : tone === "error" ? "var(--voice-status-error-dot, #EF4444)" : "var(--voice-status-ended-dot, #9CA3AF)";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        flex: 1,
        minHeight: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: isCompact ? "0 16px" : "0 24px"
      },
      children: [
        /* @__PURE__ */ jsx(VoiceBlob, { state: blobState, size: BLOB_SIZE }),
        label ? /* @__PURE__ */ jsxs(
          "div",
          {
            "aria-live": "polite",
            style: {
              marginTop: 16,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 14,
              fontWeight: 400,
              color: textColor,
              fontFamily,
              letterSpacing: 0.2,
              textAlign: "center"
            },
            children: [
              /* @__PURE__ */ jsx(
                "span",
                {
                  "aria-hidden": true,
                  style: {
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: dotColor,
                    boxShadow: `0 0 6px ${dotColor}`,
                    flexShrink: 0
                  }
                }
              ),
              label
            ]
          }
        ) : null
      ]
    }
  );
};
const VoiceCallOverlay = ({
  conversationId,
  open,
  onClose,
  pdpStacklineSku,
  containerScoped = false
}) => {
  if (!open) return null;
  if (!conversationId) return null;
  const shellStyle = containerScoped ? {
    position: "absolute",
    inset: 0,
    zIndex: 1,
    background: "var(--color-background, #FFFFFF)",
    display: "flex",
    flexDirection: "column",
    borderRadius: "inherit",
    overflow: "hidden"
  } : {
    position: "fixed",
    inset: 0,
    zIndex: 100,
    // decisions/078 — themed background matches the text chat
    // surface. Brand override applies via the widget theme's
    // `--color-background` (same var text chat root uses).
    background: "var(--color-background, #FFFFFF)",
    display: "flex",
    flexDirection: "column"
  };
  return /* @__PURE__ */ jsx(VoiceThemeProvider, { children: /* @__PURE__ */ jsx("div", { style: shellStyle, role: "dialog", "aria-modal": "true", "aria-label": "Voice call", children: /* @__PURE__ */ jsx(
    VoiceCallOverlayInner,
    {
      conversationId,
      pdpStacklineSku,
      containerScoped,
      onEnded: (info) => onClose({ reason: info.reason, durationSec: info.durationSec }),
      onUserEndClick: () => onClose({ reason: "user_dismissed_error" })
    }
  ) }) });
};
const pageStyle = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  width: "100%",
  background: "transparent"
};
const liveScrollWrapStyle = {
  position: "relative",
  flex: "1 1 auto",
  minHeight: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden"
};
const Overlay = ({ title, body, primary, containerScoped }) => /* @__PURE__ */ jsx(
  "div",
  {
    style: {
      position: containerScoped ? "absolute" : "fixed",
      inset: 0,
      background: "rgba(17,24,39,0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24,
      zIndex: containerScoped ? 2 : 50
    },
    role: "dialog",
    "aria-modal": "true",
    children: /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          background: "var(--voice-page-bg, #FFFFFF)",
          borderRadius: 16,
          padding: "24px 28px",
          maxWidth: 360,
          width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)"
        },
        children: [
          /* @__PURE__ */ jsx("div", { style: { fontSize: 18, fontWeight: 600, marginBottom: 8, color: "var(--voice-header-fg, #374151)" }, children: title }),
          /* @__PURE__ */ jsx("div", { style: { fontSize: 14, color: "var(--voice-header-fg, #6B7280)", opacity: 0.8, marginBottom: 18 }, children: body }),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              onClick: primary.onClick,
              style: {
                appearance: "none",
                border: "none",
                background: "var(--voice-end-bg, #111827)",
                color: "var(--voice-end-fg, #FFFFFF)",
                borderRadius: 999,
                padding: "10px 16px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer"
              },
              children: primary.label
            }
          )
        ]
      }
    )
  }
);
export {
  VoiceCallOverlay
};
//# sourceMappingURL=VoiceCallOverlay.js.map
